"use client"

import React, { ReactNode, useState } from "react"
import styled, { css } from "styled-components"
import { Label, P } from "@jobintrans/base-ui.components.atoms.typography"
import { Box } from "@jobintrans/base-ui.components.atoms._atoms"
import { BaseStyles } from "@jobintrans/base-ui.types.base-styles"
import Icon from "@jobintrans/base-ui.components.atoms.icon"
import * as Tooltip from "@radix-ui/react-tooltip"

export type TagProps = BaseStyles & {
  label?: string | ReactNode
  sublabel?: string
  icon?: string
  tooltip?: string
  $type?: "primary" | "secondary"
  $border?: boolean
  $iconColor?: string
  $borderColor?: string
  $color?: string
  $showBorder?: boolean
  $iconSize?: string
}

const Tag = ({
  label,
  sublabel,
  icon,
  tooltip,
  $type = "primary",
  $color,
  $iconColor,
  $iconSize = "L",
  $borderColor,
  $showBorder = true,
  ...rest
}: TagProps) => {
  const [openPopover, setOpenPopover] = useState(false)

  const handlePopoverEnter = () => {
    setOpenPopover(true)
  }

  const handlePopoverLeave = () => {
    setOpenPopover(false)
  }

  return (
    <Wrapper
      $type={$type}
      $showBorder={$showBorder}
      $borderColor={$borderColor}
      $hasLabel={!!label}
      $isIcon={!!icon || !!tooltip}
      {...rest}
    >
      {icon && <Icon icon={icon} $color={$iconColor ?? $color} $size={$iconSize} />}
      {(label || sublabel) && (
        <Box $width="100%" style={{ flex: "1" }}>
          {sublabel && (
            <Label $type="third" $color={$iconColor} $marginBottom="XXS">
              {sublabel}
            </Label>
          )}
          {typeof label === "string" || typeof label === "number" ? (
            <Label $type="secondary" $color={$color}>
              {label}
            </Label>
          ) : (
            <>{label}</>
          )}
        </Box>
      )}
      {tooltip && (
        <>
          <Tooltip.Provider>
            <Tooltip.Root open={openPopover}>
              <Tooltip.Trigger
                asChild
                onMouseEnter={handlePopoverEnter}
                onMouseLeave={handlePopoverLeave}
              >
                <TooltipButton>
                  <Icon icon="info-circle" $size="M" />
                </TooltipButton>
              </Tooltip.Trigger>

              <Tooltip.Portal>
                <Tooltip.Content
                  sideOffset={0}
                  onMouseEnter={handlePopoverEnter}
                  onMouseLeave={handlePopoverLeave}
                  style={{ zIndex: 999 }}
                  collisionPadding={10}
                >
                  <Tooltip.Arrow style={{ fill: "white", zIndex: 999 }} width={10} height={5} />
                  <TooltipWrapper>
                    <P $small $marginBottom="0" $color="second">
                      {tooltip}
                    </P>
                  </TooltipWrapper>
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        </>
      )}
    </Wrapper>
  )
}

export default Tag

const TooltipButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  height: max-content;

  svg {
    display: block !important;
  }
`

const TooltipWrapper = styled(Box)`
  background: white;
  padding: ${({ theme }) => theme.space.XS};
  border-radius: ${({ theme }) => theme.variable.borderRadius};
  box-shadow: ${({ theme }) => theme.variable.boxShadow};
  max-width: calc(100vw - 20px);
`

const Wrapper = styled(Box)<{
  $type: "primary" | "secondary"
  $showBorder: boolean
  $hasLabel: boolean
  $borderColor?: string
  $isIcon?: boolean
}>`
  width: ${({ $width = "max-content" }) => $width};
  display: flex;
  align-items: center;
  padding: 0;
  border-radius: 3px;
  transition: all 0.3s;
  border: none;

  label {
    transition: all 0.3s;
    cursor: pointer !important;
  }

  ${({ $type, $showBorder, $borderColor = "border" }) =>
    $type === "secondary" &&
    css`
      padding: ${({ theme }) => theme.space.XS} ${({ theme }) => theme.space.S};
      border-radius: ${({ theme }) => theme.variable.borderRadius};

      ${$showBorder &&
      css`
        border: 1px solid ${({ theme }) => theme.color[$borderColor]};
      `}
    `};

  ${({ $hasLabel }) =>
    !$hasLabel &&
    css`
      svg {
        margin-right: 0 !important;
      }
    `};

  ${({ $isIcon }) =>
    $isIcon &&
    css`
      display: flex;
      justify-content: flex-start;
      grid-gap: ${({ theme }) => theme.space.XS};
      align-items: center;
    `}
`
